import { Grid, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, TextField } from "@material-ui/core";
import React, { Fragment, FunctionComponent, useEffect } from 'react';
import TcmCoverLetterEntity from "../../../../../../entities/tcm/TcmCoverLetter.Entity";
import { EmptyDataLabel } from "../../../../../generic-components/empty-data-label/EmptyDataLabel.Component";
import styles from "./TcmCoverLetter.Styles";

interface ITcmCoverLetterProps {
    coverLetterState?: TcmCoverLetterEntity;
    handleTcmCoverLetterEdit?: (edit: TcmCoverLetterEntity) => void;
}

export const TcmCoverLetter: FunctionComponent<ITcmCoverLetterProps> = (props) => {
    const classes = styles();
    const abortController = new AbortController();

    const [coverLetterState, setCoverLetterState] = React.useState<TcmCoverLetterEntity>(() => {
        // Initialize with default values if no data provided
        return props.coverLetterState || {
            tcmCoverLetterId: 0,
            isCoverLetterRequired: true,
            coverLetterWillBeProvidedSeparately: null,
            prepareMedicalSummary: null,
            detailedHistoryDiagnosisPrognosis: null,
            casualRelationship: null,
            treatmentReasonableNecessary: null,
            workCapabilities: null,
            reachedMMI: null,
            permanentImpairmentRating: null,
            furtherTreatmentRecommendations: null,
            additionalQuestions: null,
        } as TcmCoverLetterEntity;
    });

    useEffect(() => {
        // Update local state when props change
        if (props.coverLetterState) {
            setCoverLetterState(props.coverLetterState);
        } else if (props.handleTcmCoverLetterEdit) {
            // Initialize with default if no data exists
            const defaultCoverLetter: TcmCoverLetterEntity = {
                tcmCoverLetterId: 0,
                isCoverLetterRequired: true,
            } as TcmCoverLetterEntity;
            props.handleTcmCoverLetterEdit(defaultCoverLetter);
        }

        return function cleanup() {
            abortController.abort();
        }
    }, [props.coverLetterState]);

    const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        event.persist();
        if (!event.target.id && !event.target.name) return;

        let newCoverLetter: TcmCoverLetterEntity = { ...coverLetterState };
        const fieldName = event.target.id || event.target.name;
        const value = event.target.value;

        if (event.target.type === 'radio') {
            newCoverLetter[fieldName] = value === "true";
        } else {
            newCoverLetter[fieldName] = value;
        }

        setCoverLetterState(newCoverLetter);

        // If cover letter is not required, clear all text fields
        if (!newCoverLetter.isCoverLetterRequired) {
            newCoverLetter.coverLetterWillBeProvidedSeparately = null;
            newCoverLetter.prepareMedicalSummary = null;
            newCoverLetter.detailedHistoryDiagnosisPrognosis = null;
            newCoverLetter.casualRelationship = null;
            newCoverLetter.treatmentReasonableNecessary = null;
            newCoverLetter.workCapabilities = null;
            newCoverLetter.reachedMMI = null;
            newCoverLetter.permanentImpairmentRating = null;
            newCoverLetter.furtherTreatmentRecommendations = null;
            newCoverLetter.additionalQuestions = null;
        }

        if (props.handleTcmCoverLetterEdit) {
            props.handleTcmCoverLetterEdit(newCoverLetter);
        }
    };

    return (
        <Fragment>
            {coverLetterState ?
                <Grid container spacing={3} className={classes.gridContainer}>
                    {/* Cover Letter Required */}
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <div className={classes.singleRowField}>
                                <FormLabel component="legend">Cover Letter Required</FormLabel>
                                <RadioGroup 
                                    row 
                                    aria-label="position" 
                                    name="isCoverLetterRequired" 
                                    id="isCoverLetterRequired" 
                                    onChange={(event) => handleTextFieldChange(event)} 
                                    value={coverLetterState.isCoverLetterRequired ?? true}
                                    className={classes.singleRowRadioButtonGroup}
                                >
                                    <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                                    <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
                                </RadioGroup>
                            </div>
                        </FormControl>
                    </Grid>

                    {/* Cover Letter Will Be Provided Separately */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                multiline={true}
                                maxRows={4}
                                label="Cover Letter Will Be Provided Separately"
                                id="coverLetterWillBeProvidedSeparately"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.coverLetterWillBeProvidedSeparately || ''}
                                inputProps={{ maxLength: 2500 }}
                                helperText={`${coverLetterState.coverLetterWillBeProvidedSeparately?.length || 0}/${2500}`}
                            />
                        </FormControl>
                    </Grid>

                    {/* Prepare Medical Summary */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Prepare Medical Summary"
                                id="prepareMedicalSummary"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.prepareMedicalSummary || ''}
                                helperText="Please prepare a medical summary"
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Detailed History, Diagnosis and Prognosis */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Detailed History, Diagnosis and Prognosis"
                                id="detailedHistoryDiagnosisPrognosis"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.detailedHistoryDiagnosisPrognosis || ''}
                                helperText="Please provide a detailed history, diagnosis and prognosis"
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Casual Relationship */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Casual Relationship"
                                id="casualRelationship"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.casualRelationship || ''}
                                helperText="Please address casual relationship. Are the claimant's current complaints casually related to the date of accident?"
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Treatment Reasonable and Necessary */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Treatment Reasonable and Necessary"
                                id="treatmentReasonableNecessary"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.treatmentReasonableNecessary || ''}
                                helperText="Has the claimant's medical treatment thus far been reasonable, necessary and casually related to the date of accident?"
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Work Capabilities */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Work Capabilities"
                                id="workCapabilities"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.workCapabilities || ''}
                                helperText="Please address the claimant's work capabilities. Is the claimant able to work full or light duty at this time? Please specify any work restrictions you feel are necessary and indicate how long these restrictions should remain in place."
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Reached MMI */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Reached Maximum Medical Improvement (MMI)"
                                id="reachedMMI"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.reachedMMI || ''}
                                helperText="Has the claimant reached Maximum Medical Improvement? If not, when do you anticipate the claimant will reach MMI?"
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Permanent Impairment Rating */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Permanent Impairment Rating"
                                id="permanentImpairmentRating"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.permanentImpairmentRating || ''}
                                helperText="If the claimant is at MMI, please provide a permanent impairment rating according to the AMA guidelines. Please apportion your rating to include any pre-existing and/or unrelated conditions."
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Further Treatment Recommendations */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Further Treatment Recommendations"
                                id="furtherTreatmentRecommendations"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.furtherTreatmentRecommendations || ''}
                                helperText="Do you have any further medical treatment recommendations for this claimant? If so, please elaborate on the specific treatment(s) and duration of the treatment plan."
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Additional Questions */}
                    <Grid item xs={12} className={classes.textFieldSection}>
                        <FormControl className={classes.selectInput}>
                            <TextField 
                                disabled={!coverLetterState.isCoverLetterRequired}
                                maxRows={6}
                                multiline={true}
                                label="Additional Questions"
                                id="additionalQuestions"
                                onChange={(event) => handleTextFieldChange(event)}
                                value={coverLetterState.additionalQuestions || ''}
                                helperText="Please include any additional questions or comments"
                                inputProps={{ maxLength: 2500 }}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                :
                <EmptyDataLabel />
            }
        </Fragment>
    );
};

